import React, { useState, useEffect } from "react";
import { Modal, Nav, Tab, Table, Spinner, Badge } from "react-bootstrap";
import AuthService from "services/auth.service";
import ChatSessionService from "services/chat-session.service";
import moment from "moment";
import IconHealthInfo from "assets/icons/healthInfoIcon.svg";
import IconArrowDown from "assets/icons/expandAllIcon.svg";
import ManualActivityCalorieTable from "./ManualActivityCalorieTable";

const ModalHealthInformation = ({ chat }) => {
  const HealthInformationTypes = [
    { type: "core_metrics", name: "CORE METRICS" },
    { type: "metabolic_health", name: "METABOLIC HEALTH" },
    { type: "manual_activity", name: "MANUAL ACTIVITY" },
    { type: "sleep", name: "SLEEP" },
  ];

  const METRIC_LABELS = {
    bmi: "BMI",
    blood_pressure: "Blood Pressure",
    blood_glucose_fasting: "Blood Glucose (Fasting)",
    blood_glucose: "Blood Glucose (Non-Fasting)",
    blood_glucose_hba1c: "Blood Glucose (HbA1c)",
    total_cholesterol: "Total Cholesterol",

    muscle_mass: "Muscle Mass",
    body_fat: "Body Fat",
    visceral_fat: "Visceral Fat",

    sleep: "Sleep",
  };

  const METRIC_HINTS = {
    bmi: "Normal: 18.5 - 24.9 kg/m²",
  };

  const [show, setShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [tableData, setTableData] = useState({});
  const [healthDataType, setHealthDataType] = useState("core_metrics");
  const [expandedRows, setExpandedRows] = useState({});

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const isCalorieGrid = tableData?.view === "daily_calorie_grid";
  const hasMetricRows = tableData && Object.keys(tableData).length > 0;

  useEffect(() => {
    if (!chat?.user_info?.id) return;

    const params = {
      user_id: chat.user_info.id,
      type: healthDataType,
    };

    getHealthMetricsTableData(params);
  }, [chat?.user_info?.id, healthDataType]);

  function getHealthMetricsTableData(params) {
    setLoader(true);
    ChatSessionService.getHealthMetricsTableData(params)
      .then(
        (data) => {
          setTableData(data || {});
        },
        (error) => {
          if (error.response && error.response.status === 401) {
            AuthService.logout();
            window.location.replace("/eb/login");
          }
        },
      )
      .finally(() => setLoader(false));
  }

  return (
    <>
      <button className="btn health-information-btn" onClick={handleShow}>
        <div className="header-title">
          <img
            src={IconHealthInfo}
            alt="icon-info-circle"
            className="title-icon"
          />
          <div className="title">Health Information</div>
        </div>
        <div>
          <img src={IconArrowDown} alt="icon-arrow-down" />
        </div>
      </button>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        className="metric-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Health Information</Modal.Title>
        </Modal.Header>

        {loader && (
          <div className="loader conditional-display">
            <Spinner animation="border" className="spinner" />
          </div>
        )}

        {!loader && (
          <Modal.Body className="conditional-display">
            <Tab.Container
              id="health-metrics-tabs"
              defaultActiveKey={healthDataType}
              onSelect={(key) => {
                setHealthDataType(key);
                setExpandedRows({});
              }}
            >
              <Nav variant="pills" defaultActiveKey={healthDataType}>
                <div className="custom-tab-items">
                  {HealthInformationTypes.map((item) => (
                    <Nav.Item key={item.type}>
                      <Nav.Link eventKey={item.type}>{item.name}</Nav.Link>
                    </Nav.Item>
                  ))}
                </div>
              </Nav>

              <Tab.Content>
                {HealthInformationTypes.map((item) => (
                  <Tab.Pane eventKey={item.type} key={item.type}>
                    {item.type !== healthDataType ? null : isCalorieGrid ? (
                      <ManualActivityCalorieTable rows={tableData.rows} />
                    ) : hasMetricRows ? (
                      <Table
                        className="metric-table"
                        style={{ borderRadius: "8px", overflow: "hidden" }}
                      >
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Date</th>
                            <th>Value</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(tableData).map(
                            ([metricKey, rows]) => {
                              const metricLabel =
                                METRIC_LABELS[metricKey] || metricKey;
                              const metricHint = METRIC_HINTS[metricKey];
                              const isExpanded =
                                expandedRows[metricKey] || false;

                              if (!rows || rows.length === 0) {
                                return (
                                  <tr
                                    key={metricKey}
                                    className="metric-row empty"
                                  >
                                    <td className="metric-label">
                                      {metricLabel}
                                      {metricHint && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "#4E4E4E",
                                          }}
                                        >
                                          {metricHint}
                                        </div>
                                      )}
                                    </td>
                                    <td>-</td>
                                    <td>-</td>
                                    <td>-</td>
                                    <td>
                                      <Badge>
                                        <button
                                          type="button"
                                          className={`recent-data-toggle`}
                                          disabled={true}
                                        >
                                          {"▼ Show Recent Data"}
                                        </button>
                                      </Badge>
                                    </td>
                                  </tr>
                                );
                              }

                              const visibleRows = isExpanded
                                ? rows
                                : rows.slice(0, 1);

                              return visibleRows.map((row, idx) => (
                                <tr
                                  key={`${metricKey}-${idx}`}
                                  className={`metric-row ${row.status
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                >
                                  {idx === 0 && (
                                    <td
                                      className="metric-label"
                                      rowSpan={visibleRows.length}
                                    >
                                      {metricLabel}
                                      {metricHint && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "#4E4E4E",
                                          }}
                                        >
                                          {metricHint}
                                        </div>
                                      )}
                                    </td>
                                  )}

                                  <td>
                                    {row.date
                                      ? moment(row.date).format("DD-MM-YYYY")
                                      : "-"}
                                  </td>

                                  <td>
                                    <div>{row.value}</div>
                                    {metricLabel === "BMI" && row.value_one && (
                                      <span
                                        style={{
                                          color: "#4E4E4E",
                                          opacity: 0.8,
                                          position: "superscript",
                                          fontSize: "14px",
                                        }}
                                      >
                                        • Weight: {row.value_one}
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      backgroundColor:
                                        row.level_background_color,
                                      maxWidth: "15%",
                                    }}
                                  >
                                    {row.status}
                                  </td>

                                  {idx === 0 && (
                                    <td
                                      rowSpan={visibleRows.length}
                                      className="align-top"
                                    >
                                      <Badge>
                                        <button
                                          type="button"
                                          className={`recent-data-toggle ${
                                            isExpanded
                                              ? "expanded"
                                              : "collapsed"
                                          }`}
                                          onClick={() =>
                                            setExpandedRows((prev) => ({
                                              ...prev,
                                              [metricKey]: !prev[metricKey],
                                            }))
                                          }
                                          disabled={rows.length <= 1}
                                        >
                                          {isExpanded
                                            ? "▲ Hide Recent Data"
                                            : "▼ Show Recent Data"}
                                        </button>
                                      </Badge>
                                    </td>
                                  )}
                                </tr>
                              ));
                            },
                          )}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="no-result text-center">
                        No data available
                      </div>
                    )}
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Tab.Container>
            <div className="health-information-note">
              {isCalorieGrid
                ? "BookDoc displays up to the 5 most recent days with calorie activity. Balance = Target − Consumed + Burned."
                : "BookDoc displays up to the 5 most recent records for each metric. Older data may not be shown in this view."}
            </div>
          </Modal.Body>
        )}
      </Modal>
    </>
  );
};

export default React.memo(ModalHealthInformation);
